import { useImpressionTracker } from "impression-tracker-react-hook";
import React from "react";
import { v4 as uuid } from "uuid";

import { ActivityListing } from "../ecommerce/types";
import useProductClicked from "../ecommerce/useProductClicked";
import { LogSearchActionTypes } from "./useLogSearchAction";
import usePromotedEventLogger from "./usePromotedEventLogger";

export default function useLogSearchImpression({
  enabled = true,
  listing,
  listingIndex,
  ...properties
}: {
  enabled?: boolean;
  listing: ActivityListing & {
    insertionId?: string;
  };
  listingIndex?: number;
  pageLocation: string;
} & Record<string, any>): {
  searchImpressionRef: React.RefCallback<Element>;
  trackSearchImpressionClick: () => void;
} {
  const eventLogger = usePromotedEventLogger(properties);

  const [searchImpressionRef, impressionId] = useImpressionTracker({
    enable: enabled,
    contentId: listing.activity.uid,
    insertionId: listing.insertionId,
    handleError: eventLogger.handleError,
    logImpression: eventLogger.logImpression,
  });

  const logSearchAction = React.useCallback(
    (actionType: LogSearchActionTypes) => {
      eventLogger.logAction({
        contentId: listing.activity.uid,
        actionId: uuid(),
        actionType,
        impressionId,
      });
    },
    [eventLogger, listing, impressionId]
  );

  const trackProductClicked = useProductClicked();

  const trackSearchImpressionClick = React.useCallback(() => {
    logSearchAction(LogSearchActionTypes.NAVIGATE);
    trackProductClicked(listing, listingIndex);
  }, [logSearchAction, trackProductClicked, listing, listingIndex]);

  return {
    searchImpressionRef,
    trackSearchImpressionClick,
  };
}
